import type { AxiosResponse } from "axios";
import { AppleAuthenticationButtonType } from "expo-apple-authentication";
import * as Device from "expo-device";
import { Link } from "expo-router";
import { useEffect, useState } from "react";
import { Text, View } from "react-native";
import { Button } from "~/components/Button";
import { type AppleCredentialResponse, AppleLoginButton } from "~/components/form/auth/AppleLoginButton";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { PasswordInput } from "~/components/form/PasswordInput";
import { TextInput } from "~/components/form/TextInput";
import { GuestFieldGroup } from "~/components/layout/guest/GuestFieldGroup";
import { Page } from "~/components/layout/Page";
import { Spacer } from "~/components/layout/Spacer";
import { BaseText, Colour, Title } from "~/components/layout/text";
import { QENDO_LINKS } from "~/constants/environment";
import { Gap, Padding } from "~/constants/layout";
import { type ApiError, useApi } from "~/providers/ApiProvider";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useOnboarding } from "~/providers/OnboardingProvider";
import { type TokenResponse, useSession } from "~/providers/SessionProvider";
import { empty, filled, isEmail } from "~/util";

type signupMethod = "email" | "facebook" | "apple";

export default function SignUp() {
    const { client } = useApi();
    const { authorize, setToken } = useSession();
    const { show, dismiss } = useOnboarding();
    const [loading, setLoading] = useState<signupMethod | null>(null);
    const [name, setName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [consented, setConsented] = useState<boolean>(false);
    const errorBag = useErrorBag<"name" | "email" | "password" | "social" | "consented">();
    const device_name = Device.deviceName ?? Device.modelName ?? `${Device.osName} ${Device.osVersion}`;

    // biome-ignore lint/correctness/useExhaustiveDependencies( errorBag.empty): error bag
    useEffect(() => {
        errorBag.empty();
        setConsented(false);
        setPassword("");
    }, []);

    const canSubmit =
        isEmail(email) &&
        filled(password) &&
        filled(name) &&
        consented &&
        empty(loading) &&
        !errorBag.hasAny(["name", "password", "email"]);

    const onFocus = () => {
        errorBag.forget("social");
    };

    const onRegister = (response: AxiosResponse<TokenResponse>) => {
        setToken(response.data.token);
        authorize();
        dismiss("welcome_back");
        show("cycle");
    };

    const onError = (error: ApiError) => {
        errorBag.add("email", error.response?.data?.message ?? "Could not register your account.");
    };

    const register = async () => {
        if (!canSubmit) {
            return;
        }

        setLoading("email");

        await client()
            .post("/register", {
                email,
                password,
                name,
                device_name,
                consent_granted: consented,
            })
            .then(onRegister)
            .catch(onError)
            .finally(() => {
                setLoading(null);
            });
    };

    const _onFacebookRegister = async (code: string) => {
        await client()
            .post("/registered/facebook", {
                code,
                device_name,
                consent_granted: consented,
            })
            .then(onRegister)
            .catch(onError)
            .finally(() => {
                setLoading(null);
            });
    };

    const onAppleRegister = async (response: AppleCredentialResponse) => {
        await client()
            .post("/registered/apple", {
                ...response,
                name: response.name ?? name,
                device_name,
                consent_granted: consented,
            })
            .then(onRegister)
            .catch(onError)
            .finally(() => {
                setLoading(null);
            });
    };

    return (
        <Page gradient spacer>
            <GuestFieldGroup>
                <View style={Gap.xs}>
                    <Title size="xl">Hi there</Title>
                    <BaseText size="lg">Create an account to get started</BaseText>
                </View>
                <TextInput
                    name="name"
                    label="Name"
                    autoComplete="name"
                    value={name}
                    onChangeText={setName}
                    disabled={filled(loading)}
                    onFocus={onFocus}
                />
                <TextInput
                    name="email"
                    label="Email Address"
                    autoComplete="username"
                    autoCapitalize={"none"}
                    textContentType="emailAddress"
                    value={email}
                    onChangeText={setEmail}
                    disabled={filled(loading)}
                    onFocus={onFocus}
                />
                <PasswordInput
                    name="password"
                    autoComplete="new-password"
                    autoCapitalize={"none"}
                    value={password}
                    onChangeText={setPassword}
                    disabled={filled(loading)}
                    onFocus={onFocus}
                />
                <CheckboxInput
                    label={
                        <Text>
                            I agree to the QENDO app{" "}
                            <Link href={QENDO_LINKS.privacy}>
                                <Colour colour="link">Privacy Policy </Colour>
                            </Link>
                            and{" "}
                            <Link href={QENDO_LINKS.terms}>
                                <Colour colour="link">Terms of Use</Colour>
                            </Link>
                            .
                        </Text>
                    }
                    value={consented}
                    onValueChange={setConsented}
                    disabled={filled(loading)}
                    name="consent_granted"
                    required
                />
                <Button
                    title="Create account"
                    variant="primary"
                    size="lg"
                    disabled={!canSubmit}
                    onPress={register}
                    loading={loading === "email"}
                />
            </GuestFieldGroup>
            <Spacer />
            <View style={[Padding.horizontal_md, Gap.md, Padding.bottom_xxl]}>
                <AppleLoginButton
                    disabled={!consented}
                    loading={loading === "apple"}
                    type={AppleAuthenticationButtonType.SIGN_UP}
                    onAuthenticationSuccess={onAppleRegister}
                    onAuthenticationStart={() => setLoading("apple")}
                    onAuthenticationResolved={() => setLoading(null)}
                />
            </View>
        </Page>
    );
}
